import React, { useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusCircleOutlined } from '@ant-design/icons';
import { Modal, Spin } from 'antd';

import CreateOrganization from '@totem/components/userContext/login/CreateOrganization';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import NotificationContext from '@totem/components/utilities/notification/NotificationContext';
import { LoginProfile } from '@totem/types/user';
import { LOGIN_PROFILE_ENDPOINT } from '@totem/utilities/endpoints';
import logger from '@totem/utilities/logger';
import { consumePostLoginRedirect } from '@totem/utilities/loginRedirect';

import '../../login/registration.css';
const styles = {
  footer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  form: {
    width: '100%',
  },
  formItem: {
    paddingBottom: '0',
    marginBottom: '2rem',
  },
  icon: {
    marginRight: '1.5rem',
    fontSize: '24px',
  },
};
const SYSTEMS_ADMINS_ROLE = 3;
const SwitchOrganizationsDialog = () => {
  const navigate = useNavigate();
  const { notify } = useContext(NotificationContext);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [loadingToken, setLoadingToken] = useState<boolean>(false);
  const {
    userProfile,
    loading,
    setProfileAndToken,
    refetch,
    getApiToken,
    setTokenTTL,
  } = useContext(UserProfileContext);
  const { organizations } = userProfile;

  const sendOrganizationSwitch = useCallback(
    async (userId: string, newOrganizationId: string) => {
      if (!loadingToken) {
        try {
          setLoadingToken(true);

          fetch(
            `${LOGIN_PROFILE_ENDPOINT}/${userId}/${newOrganizationId}/switch`,
            {
              method: 'GET',
              headers: new Headers({
                Authorization: `Bearer ${getApiToken()}`,
              }),
            },
          )
            .then((res) => {
              if (res.status >= 400) {
                logger.error(res.statusText);
              }
              return res.json();
            })
            .then((result: LoginProfile) => {
              if (result.ttlMinutes > 0) {
                setTokenTTL(result.ttlMinutes);
              }
              setProfileAndToken(
                result.profile,
                result.token,
                'SwitchOrganizationsDialog',
              );
              const redirectPath = consumePostLoginRedirect();
              navigate(redirectPath || '/');
            })
            .then(() => {
              setLoadingToken(false);
            });
        } catch {
          notify(`Could not switch to the organization`, true);
        }
      }
    },
    [],
  );
  const navigateToOrganization = async (id: string) => {
    sendOrganizationSwitch(userProfile.id, id);
  };
  const handlePostOrganizationCreate = () => {
    refetch();

    setShowCreateDialog(false);
  };
  const isSystemsAdmin = userProfile.organizations.some(
    (org) => org.role === SYSTEMS_ADMINS_ROLE && org.isActive,
  );

  return (
    <div>
      <Modal
        open
        style={{ top: '20vh', border: 'none' }}
        styles={{ body: { border: 'none' } }}
        title={
          <div styleName="organization-switcher-modal-title">
            Select an Account
          </div>
        }
        closable={false}
      >
        <Spin spinning={loading}>
          <div styleName="organization-switcher-modal-content">
            <div styleName="organization-switcher-content-heading">
              Select an organization you would like to log into.
            </div>
            <div styleName="organization-switcher-list-container">
              {isSystemsAdmin && (
                <div
                  styleName="organization-selection-container"
                  onClick={() => setShowCreateDialog(true)}
                >
                  <PlusCircleOutlined style={styles.icon} />
                  <div styleName="organization-selection-name">
                    Create a new account
                  </div>
                </div>
              )}
              {organizations
                .filter((org) => org.isActive)
                .map((org) => (
                  <div
                    styleName="organization-selection-container"
                    onClick={() => navigateToOrganization(org.id)}
                    key={org.id}
                  >
                    <div styleName="organization-selection-name">
                      {org.name}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </Spin>
      </Modal>
      {showCreateDialog && (
        <CreateOrganization
          open={showCreateDialog}
          onCancel={() => setShowCreateDialog(false)}
          onComplete={handlePostOrganizationCreate}
        />
      )}
    </div>
  );
};
export default SwitchOrganizationsDialog;
